import { useState } from 'react';

import { ControlledMenu, MenuButton, MenuItem, useClick } from '@szhsin/react-menu';
import cn from 'classnames';

import { isAuthenticated } from 'actions/user';
import { CommunitySection } from 'components/markets/partials/quickLinks/MobileQuickLinksIndian/CommunitySection';
import MenuSection from 'components/MenuSection';
import Render from 'components/render';
import {
  ANDROID_APK_URL,
  DELTA_EXCHANGE_YOUTUBE_CHANNEL,
  IOS_APP_INDIA_URL,
  PLAY_STORE_URL,
} from 'constants/constants';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import useLockedBody from 'hooks/useLockedBody';
import i18n from 'i18n/config';
import { useAppSelector } from 'storeHooks';
import AppStore from 'styles/assets/appStoreBadge.png';
import DeltaIndiaMobileAppQrCode from 'styles/assets/DeltaIndiaMobileAppQrCode.svg';
import DownloadAndroid from 'styles/assets/downloadandroid.svg';
import GooglePlay from 'styles/assets/googlePlayBadge.png';
import { NineSquareIcon, YoutubeRedIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';

import { getMixpanelPageName } from '../../universalSearch/partials/helpers';
import styles from './style.module.scss';

const YTLinkText = i18n.t('headerFooter:header.sideMenu.youtubeLinkText');
const RESOURCES_TEXT = i18n.t('headerFooter:header.sideMenu.resources');
const HELP_TEXT = i18n.t('headerFooter:header.sideMenu.help');

const YoutubeLink = () => {
  const handleClick = () => {
    window.open(DELTA_EXCHANGE_YOUTUBE_CHANNEL);
  };

  return (
    <Box
      flex="flex"
      alignItems="center"
      onClick={handleClick}
      className={styles.ytLinkContainer}
      data-testid="SideMenuYoutubeLink"
    >
      <Box>
        <YoutubeRedIcon width={20} />
      </Box>
      <Box className={styles.youtubeLinkText}>{YTLinkText}</Box>
    </Box>
  );
};

const MobileAppAndQrCode = () => {
  const handleClick = mixPanelEvent => {
    trackMixpanelEvent(mixPanelEvent, {
      page_name: getMixpanelPageName(),
    });
  };

  return (
    <Box flex="flex" alignItems="center" className={styles.mobileAppQRCodeWrapper}>
      <Box className={styles.qrCode}>
        <img
          src={DeltaIndiaMobileAppQrCode}
          height={24}
          width={24}
          alt="mobile_app_qr_code"
        />
        <Box className={styles.maximizedQRCode}>
          <img
            src={DeltaIndiaMobileAppQrCode}
            height={90}
            width={90}
            alt="mobile_app_qr_code"
          />
        </Box>
      </Box>
      <Box>
        <a
          data-testid="play-store-icon"
          onClick={() => handleClick('Android Download - Header')}
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noreferrer"
        >
          <img src={GooglePlay} width="auto" height="32px" alt="google-play" />
        </a>
      </Box>
      <Box>
        <a
          data-testid="android-apk-icon"
          onClick={() => handleClick('Android Download - Header')}
          href={ANDROID_APK_URL}
          rel="noreferrer"
        >
          <img src={DownloadAndroid} width="auto" height="32px" alt="android-app" />
        </a>
      </Box>
      <Box>
        <a
          data-testid="ios-app-alternative"
          onClick={() => handleClick('IOS App Alternative - Header')}
          target="_blank"
          href={IOS_APP_INDIA_URL}
          rel="noreferrer"
        >
          <img src={AppStore} width="auto" height="32px" alt="ios-app-alternative" />
        </a>
      </Box>
    </Box>
  );
};

const PrimarySideMenuIndia = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const onMenuClose = () => setIsMenuOpen(false);
  const isLoggedIn = useAppSelector(state => isAuthenticated(state?.user));
  const { onClick, onMouseDown } = useClick(isMenuOpen, setIsMenuOpen);
  useLockedBody(isMenuOpen);

  const onMenuClick = key => {
    if (key === 'mocktrading') {
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.TESTNET_LINK_CLICKED, {
        source: 'dotted_menu',
        site_demarkation: MIXPANEL_SOURCES.WEB_APP,
      });
    }
  };

  return (
    <>
      <Box renderWhen={isMenuOpen} className={styles.overlayContainer} />

      <MenuButton
        className={cn(styles.button, {
          [styles.active]: isMenuOpen,
        })}
        onClick={onClick}
        onMouseDown={onMouseDown}
        data-testid="indian-sidemenu"
      >
        <NineSquareIcon className={styles.menuButtonIcon} />
      </MenuButton>

      <ControlledMenu
        state={isMenuOpen ? 'open' : 'closed'}
        onClose={onMenuClose}
        className={styles.menuContainer}
      >
        <Render when={isLoggedIn}>
          <MenuItem
            className={cn(styles.menuItemContainer, styles.appIcons)}
            data-testid="indian-sidemenu-app-links"
          >
            <MobileAppAndQrCode />
          </MenuItem>
        </Render>

        <MenuItem
          className={cn(styles.menuItemContainer, styles.ytLink)}
          data-testid="indian-sidemenu-youtube-channel-link"
        >
          <YoutubeLink />
        </MenuItem>

        <MenuItem
          className={cn(styles.menuItemContainer, styles.ytLink)}
          data-testid="indian-sidemenu-community-section"
        >
          <CommunitySection />
        </MenuItem>

        <MenuItem
          className={styles.menuItemContainer}
          data-testid="indian-sidemenu-resource-menu"
        >
          <Box className={styles.menuHeader}>{RESOURCES_TEXT}</Box>

          <MenuSection
            section="resources"
            classnames={{
              root: styles.resourcesMenuContainer,
              option: styles.resourcesMenu,
              label: styles.label,
              arrow: styles.rightArrow,
            }}
            onMenuClick={onMenuClick}
          />
        </MenuItem>

        <MenuItem
          className={styles.menuItemContainer}
          data-testid="indian-sidemenu-help-menu"
        >
          <Box className={styles.menuHeader}>{HELP_TEXT}</Box>

          <MenuSection
            section="help"
            classnames={{
              root: styles.helpMenuContainer,
              option: styles.helpMenu,
              label: styles.label,
              arrow: styles.rightArrow,
            }}
          />
        </MenuItem>
      </ControlledMenu>
    </>
  );
};

export { YoutubeLink };
export default PrimarySideMenuIndia;
